/**
 * Pure helpers for PanelHeader — extracted for unit testing.
 *
 * ageClass() drives the CSS freshness chip on every panel section header.
 * Getting the thresholds wrong silently shows stale colours to operators.
 */

import type { FallbackTier } from '@sikhio/shared';

/**
 * Map data age + fallback tier → CSS freshness class.
 *
 * Tier overrides take priority:
 *   unavailable → data-age--crit (offline / missing API key)
 *   scenario    → data-age--warn (synthetic / pre-generated data)
 *
 * Minute thresholds (when tier is live/cache):
 *   < 5   → data-age--fresh (green)
 *   < 30  → ""              (neutral — normal polling window)
 *   < 120 → data-age--warn  (yellow)
 *   ≥ 120 → data-age--stale (red)
 */
export function ageClass(minutes?: number | null, tier?: FallbackTier): string {
  if (tier === 'unavailable') return 'data-age--crit';
  if (tier === 'scenario') return 'data-age--warn';
  if (minutes == null) return '';
  if (minutes < 5) return 'data-age--fresh';
  if (minutes < 30) return '';
  if (minutes < 120) return 'data-age--warn';
  return 'data-age--stale';
}

/**
 * Whether `ageMinutes` describes how old the READING is (true) or merely how
 * long ago we fetched it (false).
 *
 * `live` and `cache` both carry a real observation time, so rendering the age —
 * including fmtAge's "LIVE" for sub-minute — is truthful. For `database`,
 * `reference` and `scenario` the age is only fetch recency: the yearly RID
 * reservoir snapshot fetched 10 seconds ago was rendering as "DB · LIVE",
 * which reads as "this reservoir number is current" when it is a พ.ศ. 2568
 * annual figure. Those tiers show the tier badge alone.
 */
export function ageIsMeaningful(tier?: FallbackTier): boolean {
  return tier == null || tier === 'live' || tier === 'cache';
}

/**
 * Age text for a panel header, given the tier.
 *
 * `fmtAge` renders anything under a minute as the word "LIVE" — correct for a
 * live feed, but a cached copy fetched 10 seconds ago was rendering as
 * "CACHE · LIVE", which reads as a contradiction. Only a genuinely live tier
 * may say LIVE; a cache shows its numeric age so an operator can see how
 * stale it is.
 */
export function ageLabel(
  minutes: number | null | undefined,
  tier: FallbackTier | undefined,
  fmt: (m: number | null | undefined) => string,
): string {
  const text = fmt(minutes);
  if (text === 'LIVE' && tier != null && tier !== 'live') return '<1m';
  return text;
}
